import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PasswordStrength } from '@/components/ui/PasswordStrength';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError, apiFetch, getErrorMessage } from '@/lib/api';
import { passwordStrength } from '@/lib/password-strength';
import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

function normalizeCode(value: string) {
  return value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 5);
}

export function ForgotPasswordPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState(() => searchParams.get('email') ?? '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  const strength = useMemo(() => passwordStrength(password), [password]);

  if (!loading && user) return <Navigate to="/" replace />;

  async function requestCode(nextEmail: string) {
    await apiFetch('/auth/password/forgot', {
      method: 'POST',
      body: JSON.stringify({ email: nextEmail }),
    });
  }

  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setInfo('');
    setSubmitting(true);
    try {
      await requestCode(email.trim());
      setStep(2);
      setInfo('Se o e-mail existir, enviamos um código de 5 caracteres. Confira a caixa de entrada e o spam.');
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível enviar o código.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    setError('');
    setResending(true);
    try {
      await requestCode(email.trim());
      setInfo('Se o e-mail existir, enviamos um novo código.');
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível reenviar o código.'));
    } finally {
      setResending(false);
    }
  }

  async function handleReset(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (code.length !== 5) {
      setError('Informe o código de 5 caracteres enviado por e-mail.');
      return;
    }
    if (strength.passed < strength.total) {
      setError('A nova senha ainda não atende todos os requisitos.');
      return;
    }
    if (password !== passwordConfirmation) {
      setError('As senhas não coincidem.');
      return;
    }

    setSubmitting(true);
    try {
      await apiFetch('/auth/password/reset', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim(),
          code,
          password,
          password_confirmation: passwordConfirmation,
        }),
      });
      navigate('/login?senha=redefinida', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(getErrorMessage(err, 'Não foi possível redefinir a senha.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      <div
        className="relative hidden lg:flex lg:w-1/2 flex-col justify-between overflow-hidden p-12 text-white"
        style={{
          backgroundImage: "url('/images/login-hero.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-manejai-forest/85 via-manejai-forest/55 to-manejai-moss/70" />
        <div className="relative z-10">
          <p className="font-display text-3xl font-semibold">Manejai</p>
          <p className="mt-2 text-manejai-cream/90 text-lg">Gestão de rebanho com clareza</p>
        </div>
        <div className="relative z-10 space-y-4 max-w-md">
          <p className="text-manejai-cream/90 text-sm leading-relaxed">
            Informe o e-mail da conta. Enviamos um código para você criar uma senha nova — sem
            precisar falar com o suporte.
          </p>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <p className="font-display text-2xl font-semibold text-manejai-forest">Manejai</p>
            <p className="mt-1 text-sm text-manejai-bark/70">Redefinir senha</p>
          </div>

          <h1 className="font-display text-2xl font-semibold text-manejai-forest mb-2">
            {step === 1 ? 'Esqueceu a senha?' : 'Digite o código'}
          </h1>
          <p className="text-sm text-manejai-bark/70 mb-8">
            {step === 1
              ? 'Enviaremos um código de verificação para o e-mail da conta.'
              : `Código enviado para ${email}. Ele vale por 10 minutos.`}
          </p>

          {error && (
            <Alert variant="error" className="mb-4">
              {error}
            </Alert>
          )}
          {info && !error && (
            <Alert variant="info" className="mb-4">
              {info}
            </Alert>
          )}

          {step === 1 ? (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <Input
                label="E-mail"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button type="submit" className="w-full" size="lg" loading={submitting}>
                Enviar código
              </Button>
            </form>
          ) : (
            <form onSubmit={handleReset} className="space-y-4">
              <Input
                label="Código"
                autoComplete="one-time-code"
                autoCapitalize="characters"
                inputMode="text"
                spellCheck={false}
                required
                value={code}
                onChange={(e) => setCode(normalizeCode(e.target.value))}
                hint="5 caracteres, como no e-mail"
                className="font-mono tracking-[0.35em] uppercase text-center"
              />
              <div className="space-y-3">
                <Input
                  label="Nova senha"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <PasswordStrength password={password} />
              </div>
              <Input
                label="Confirmar nova senha"
                type="password"
                autoComplete="new-password"
                required
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
              />
              <Button type="submit" className="w-full" size="lg" loading={submitting}>
                Redefinir senha
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  className="font-medium text-manejai-moss hover:underline disabled:opacity-50"
                  onClick={() => {
                    setStep(1);
                    setError('');
                    setInfo('');
                    setCode('');
                    setPassword('');
                    setPasswordConfirmation('');
                  }}
                >
                  Trocar e-mail
                </button>
                <button
                  type="button"
                  className="font-medium text-manejai-moss hover:underline disabled:opacity-50"
                  onClick={() => void handleResend()}
                  disabled={resending}
                >
                  {resending ? 'Reenviando…' : 'Reenviar código'}
                </button>
              </div>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-manejai-bark/70">
            Lembrou a senha?{' '}
            <Link to="/login" className="font-medium text-manejai-moss hover:underline">
              Voltar ao login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
